import React, { useEffect, useRef, useState } from 'react'
import { Card, makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '@paintscout/ui'
import { EstimatorAvatarSelect, SelectableChipSmall, useUser, useUsers, useIsMobile } from '@paintscout/ui'
import { jsonToLucene } from 'json-to-lucene'
import TaskDocList from './TaskDocList'
import type { User } from '@paintscout/api'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import { useLocation } from 'react-router-dom'
import type { UrlParams } from 'src/components/pages/locations/Locations/Locations'
import { removeAuth0Prefix } from '@paintscout/util'

const useStyles = makeStyles<Theme, TaskListProps>(
  (theme) => ({
    root: {},
    taskDocContainer: {
      padding: `${theme.spacing(2)}px ${theme.spacing(2)}px`
    },
    buttonContainer: {
      padding: `${theme.spacing(1.75)}px ${theme.spacing(2)}px`,
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      marginBottom: theme.spacing(1),
      display: 'flex',
      gap: theme.spacing(2)
    },
    taskTableCard: {
      border: `1px solid ${theme.palette.divider}`
    }
  }),
  { name: 'TaskList' }
)

export interface TaskListProps {
  classes?: StyleClasses<typeof useStyles>
}

type TaskView = 'upcoming' | 'overdue' | 'completed' | 'all'

function TaskList(props: TaskListProps) {
  const isMobile = useIsMobile({ custom: 600 })
  const location = useLocation<{ page?: number; bookmark?: string; sort?: number }>()
  const isTablet = useIsMobile({ custom: 1024 }) && !isMobile
  const urlParams = getUrlQuery<UrlParams>({ location })
  const { users, userPictures } = useUsers()
  const { user, isSales } = useUser()
  const classes = useStyles(props)

  const [view, setView] = useState<TaskView>((urlParams?.tab as TaskView) ?? 'upcoming')
  const [selectedUsers, setSelectedUsers] = useState<User[]>(isSales ? [user] : users || [])
  const selectedUsersBeforeClick = useRef<User[]>(selectedUsers)
  const [tasksQuery, setTasksQuery] = useState(updateTasksQuery())
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState(urlParams.sort ?? 'startDateTime') // Default sort, will be overridden if we query

  function onView(newView: TaskView) {
    setView(newView)
    setPage(1)
  }

  function updateTasksQuery() {
    const userIds = selectedUsers?.map((u) => removeAuth0Prefix(u.user_id)).filter(Boolean)

    // If no users are selected, return a query that will match nothing
    if (!userIds || userIds.length === 0) {
      return jsonToLucene({
        filters: [{ type: 'equal', id: 'owner.id', value: '__none__' }]
      })
    }

    return jsonToLucene({
      filters: [
        // Filter for tasks owned by any of the selected users
        { type: 'in', id: 'owner.id', value: userIds },

        // If the view is "upcoming", filter for tasks in the future and not completed
        ...(view === 'upcoming'
          ? [
              {
                id: 'startDateTime',
                type: 'range' as const,
                value: [Date.now(), 9999999999999] as [number, number] // far future timestamp
              },
              {
                id: 'completed',
                type: 'equal' as const,
                value: false
              }
            ]
          : []),

        // If the view is "overdue", filter for tasks with a startDateTime in the past and not completed
        ...(view === 'overdue'
          ? [
              {
                id: 'startDateTime',
                type: 'range' as const,
                value: [0, Date.now()] as [number, number]
              },
              {
                id: 'completed',
                type: 'equal' as const,
                value: false
              }
            ]
          : []),

        // If the view is "completed", filter for completed tasks
        ...(view === 'completed'
          ? [
              {
                id: 'completed',
                type: 'equal' as const,
                value: true
              }
            ]
          : [])
      ]
    })
  }

  const handleUserClick = (user: User, e: React.MouseEvent) => {
    if (e.detail === 1) {
      selectedUsersBeforeClick.current = selectedUsers
    }
    if (selectedUsers.some((u) => u.user_id === user.user_id)) {
      setSelectedUsers(selectedUsers.filter((u) => u.user_id !== user.user_id))
    } else {
      setSelectedUsers([...selectedUsers, user])
    }
  }

  const handleUserDoubleClick = (allUsers: User[]) => {
    const allWereSelected = selectedUsersBeforeClick.current.length === allUsers.length
    setSelectedUsers(allWereSelected ? [] : allUsers)
  }

  const handleRequestSort = (_ev: React.MouseEvent, name: string, direction: 'asc' | 'desc') => {
    const newSort = `${direction === 'desc' ? '-' : ''}${name}`

    setPage(1)
    setUrlQuery({ location, params: { query: tasksQuery, sort: newSort } })
    setSort(newSort)
  }

  useEffect(() => {
    setTasksQuery(updateTasksQuery())
  }, [selectedUsers, view])

  useEffect(() => {
    if (users?.length > 0 && selectedUsers.length === 0 && !isSales) {
      setSelectedUsers(users)
    }
  }, [users, isSales])

  useEffect(() => {
    setPage(1)
  }, [tasksQuery])

  return (
    <Card>
      <div className={classes.buttonContainer}>
        <>
          <SelectableChipSmall label="Upcoming" selected={view === 'upcoming'} onClick={() => onView('upcoming')} />
          <SelectableChipSmall label="Overdue" selected={view === 'overdue'} onClick={() => onView('overdue')} />
          <SelectableChipSmall label="Completed" selected={view === 'completed'} onClick={() => onView('completed')} />
          <SelectableChipSmall label="All" selected={view === 'all'} onClick={() => onView('all')} />
        </>
      </div>
      {users?.length > 1 && !isSales && (
        <EstimatorAvatarSelect
          users={users.filter((u) => u.app_metadata?.active)}
          selectedUsers={selectedUsers}
          onClick={handleUserClick}
          onDoubleClick={handleUserDoubleClick}
          userPictures={userPictures}
        />
      )}
      <div className={classes.taskDocContainer}>
        <TaskDocList
          classes={classes}
          dense={isTablet}
          query={tasksQuery}
          limit={20}
          page={page}
          sort={sort}
          onPageChange={setPage}
          handleRequestSort={handleRequestSort}
          enableFade={false}
          showSinglePagePagination
          view={view}
        />
      </div>
    </Card>
  )
}

export default TaskList
